/** Create / edit one fleet vehicle in a drawer-style dialog (cross-tenant).
 *
 * Edit mode keys off the row's own tenant + fleet config. Create mode requires
 * the operator to pick a tenant (tree TenantPicker) and one of that operator's
 * fleet configs first — type/route dropdowns then scope to that tenant. Image +
 * icon uploads use the console multipart pattern. Controlled state (this surface
 * predates RHF adoption in the fleet feature; mirrors the create dialogs nearby).
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as fleetVehiclesApi from '@/api/fleetVehicles';
import { useEditorHistoryTabs } from '@/components/History/useEditorHistoryTabs';
import { usePermissions } from '@/hooks/usePermissions';
import type { Vehicle, VehicleWriteInput } from '@/api/fleetVehicles';
import * as integrationsApi from '@/api/integrations';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { useCreateDialogTenant } from '@/scope/useCreateDialogTenant';

import { DEFAULT_MARKER_COLOR } from './shared';

interface Props {
  open: boolean;
  vehicle: Vehicle | null; // null → create
  onClose: () => void;
  onSaved: () => void;
  /** The page's operators (D9), for create mode — see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
}

function ImagePicker({
  label,
  currentUrl,
  file,
  onPick,
  disabled,
}: {
  label: string;
  currentUrl: string | null;
  file: File | null;
  onPick: (f: File | null) => void;
  disabled?: boolean;
}) {
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  const src = preview ?? currentUrl;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 56, height: 56, borderRadius: 1, border: '1px solid', borderColor: 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            bgcolor: 'action.hover',
          }}
        >
          {src ? (
            <Box component="img" src={src} alt={label} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : (
            <Typography variant="caption" color="text.disabled">none</Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button component="label" size="small" variant="outlined" disabled={disabled}>
            {src ? 'Replace' : 'Upload'}
            <input hidden type="file" accept="image/*" onChange={(e) => onPick(e.target.files?.[0] ?? null)} />
          </Button>
          {file && (
            <Button size="small" onClick={() => onPick(null)} disabled={disabled}>Clear</Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

export function VehicleEditDialog({ open, vehicle, onClose, onSaved, pageTenants }: Props) {
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const isCreate = vehicle === null;
  const history = useEditorHistoryTabs({
    enabled: !isCreate && hasPerm('console_audit_view'),
    contentType: 'demo.vehicle',
    objectPk: vehicle?.id ?? null,
    onRestored: onSaved,
  });

  // Create mode: the operator picks a tenant + fleet config. Edit mode: fixed
  // from the row. `resetTenant()` on open seeds it: the page's single tenant
  // with `pageTenants`, else the console scope.
  const [tenant, setTenant, resetTenant] = useCreateDialogTenant(open, pageTenants);
  const [fleetId, setFleetId] = useState<number | ''>('');
  const [name, setName] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [typeId, setTypeId] = useState<number | ''>('');
  const [routeId, setRouteId] = useState<number | ''>('');
  const [enabled, setEnabled] = useState(true);
  const [capacity, setCapacity] = useState('0');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [iconFile, setIconFile] = useState<File | null>(null);

  const toId = isCreate ? (tenant ? Number(tenant.id) : null) : (vehicle.tenant?.id ?? null);

  const typesQuery = useQuery({
    queryKey: ['fleet-vehicle-types', toId],
    queryFn: () => fleetVehiclesApi.listTypes(toId as number),
    enabled: open && toId != null,
  });
  const routesQuery = useQuery({
    queryKey: ['fleet-vehicle-routes', toId],
    queryFn: () => fleetVehiclesApi.listRoutes(toId as number),
    enabled: open && toId != null,
  });
  const fleetsQuery = useQuery({
    queryKey: ['fleet-configs', toId],
    queryFn: () => integrationsApi.listRows(toId as number),
    enabled: open && isCreate && toId != null,
  });
  const fleetConfigs = (fleetsQuery.data?.results ?? [])
    .filter((r) => r.kind === 'fleet')
    .map((r) => ({ id: Number(r.id.replace(/^fleet:/, '')), label: r.display_name }));

  useEffect(() => {
    if (!open) return;
    history.reset();
    resetTenant();
    setFleetId('');
    setName(vehicle?.name ?? '');
    setLicensePlate(vehicle?.license_plate ?? '');
    setTypeId(vehicle?.type_id ?? '');
    setRouteId(vehicle?.route?.id ?? '');
    setEnabled(vehicle?.enabled ?? true);
    setCapacity(String(vehicle?.capacity ?? 0));
    setImageFile(null);
    setIconFile(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- history.reset is per-render
  }, [open, vehicle]);

  // Changing tenant invalidates the tenant-scoped selections.
  const onTenantChange = (t: TenantPickerValue | null) => {
    setTenant(t);
    setFleetId('');
    setTypeId('');
    setRouteId('');
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const data: VehicleWriteInput = {
        name: name.trim(),
        license_plate: licensePlate.trim() || null,
        enabled,
        capacity: Number(capacity) || 0,
        type_id: typeId === '' ? null : typeId,
        route_id: routeId === '' ? null : routeId,
      };
      const files = { image: imageFile, icon: iconFile };
      if (isCreate) {
        return fleetVehiclesApi.create(
          { ...data, tour_operator: toId as number, fleet_id: fleetId as number },
          files,
        );
      }
      return fleetVehiclesApi.update(vehicle.id, data, files);
    },
    onSuccess: () => {
      snackbar.show(isCreate ? 'Vehicle created.' : 'Vehicle saved.', 'success');
      onSaved();
      onClose();
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not save the vehicle.', 'error'),
  });

  const nameError = name.trim() === '';
  const createIncomplete = isCreate && (toId == null || fleetId === '' || typeId === '');
  const canSave = !nameError && !createIncomplete && !saveMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isCreate ? 'Add vehicle' : `Edit — ${vehicle?.name}`}</DialogTitle>
      <DialogContent dividers>
        {history.header}
        {history.historyPane ?? (
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {isCreate ? (
            <>
              <Grid item xs={12} sm={6}>
                <TenantPicker
                  value={tenant}
                  featured={pageTenants}
                  onChange={onTenantChange}
                  label="Tenant"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select required fullWidth size="small" label="Fleet config"
                  value={fleetId}
                  disabled={toId == null || fleetsQuery.isLoading}
                  helperText={toId == null ? 'Pick a tenant first' : ' '}
                  onChange={(e) => setFleetId(e.target.value === '' ? '' : Number(e.target.value))}
                >
                  {fleetConfigs.map((f) => (
                    <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Operator: <strong>{vehicle?.tenant ? (vehicle.tenant.code || vehicle.tenant.name) : '—'}</strong>
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              required fullWidth size="small" label="Name" value={name}
              error={nameError} onChange={(e) => setName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="License plate #" value={licensePlate}
              onChange={(e) => setLicensePlate(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select required={isCreate} fullWidth size="small" label="Type" value={typeId}
              disabled={toId == null}
              error={isCreate && typeId === ''}
              helperText={isCreate && typeId === '' ? 'Required' : ' '}
              onChange={(e) => setTypeId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              {(typesQuery.data?.results ?? []).map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select fullWidth size="small" label="Assigned route" value={routeId}
              disabled={toId == null} helperText=" "
              onChange={(e) => setRouteId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <MenuItem value=""><em>— None —</em></MenuItem>
              {(routesQuery.data?.results ?? []).map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: r.color || DEFAULT_MARKER_COLOR }} />
                    <span>{r.name}{!r.enabled ? ' (disabled)' : ''}</span>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" type="number" label="Default capacity" value={capacity}
              onChange={(e) => setCapacity(e.target.value)} inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
              label="Enabled"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ImagePicker label="Image" currentUrl={vehicle?.image?.url ?? null} file={imageFile} onPick={setImageFile} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ImagePicker label="Icon" currentUrl={vehicle?.icon?.url ?? null} file={iconFile} onPick={setIconFile} />
          </Grid>

          {vehicle && vehicle.tags.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Tags (read-only)
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {vehicle.tags.map((t) => (
                  <Chip key={t.id} size="small" label={t.name} variant="outlined" />
                ))}
              </Stack>
            </Grid>
          )}

          {isCreate && toId != null && !fleetsQuery.isLoading && fleetConfigs.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="warning">
                This operator has no fleet integration configured — add one in its Integrations tab first.
              </Alert>
            </Grid>
          )}
          {isCreate && toId != null && typesQuery.data && typesQuery.data.results.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="warning">
                This operator has no vehicle types defined — create one in the admin before adding vehicles.
              </Alert>
            </Grid>
          )}
        </Grid>
        )}
      </DialogContent>
      <DialogActions>
        {history.showingHistory ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={!canSave} onClick={() => saveMutation.mutate()}>
              {saveMutation.isPending ? 'Saving…' : isCreate ? 'Create' : 'Save changes'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
